import { z } from 'zod'
import { instructionSchema } from './instruction'

/** {@link https://solana.com/docs/rpc/json-structures#transactions} */
export const transactionSchema = z.object({
  /** A list of base-58 encoded signatures applied to the transaction. The list is always of length message.header.numRequiredSignatures and not empty. The signature at index i corresponds to the public key at index i in message.accountKeys. The first one is used as the transaction id. */
  signatures: z.array(z.string()),
  /** Defines the content of the transaction */
  message: z.object({
    /**  List of base-58 encoded public keys used by the transaction, including by the instructions and for signatures. The first message.header.numRequiredSignatures public keys must sign the transaction. */
    accountKeys: z.array(
      z.object({
        pubkey: z.string(),
        signer: z.boolean(),
        source: z.string(),
        writable: z.boolean(),
      }),
    ),
    /** Details the account types and signatures required by the transaction. */
    header: z
      .object({
        /** The total number of signatures required to make the transaction valid. The signatures must match the first numRequiredSignatures of message.accountKeys. */
        numRequiredSignatures: z.number(),
        /** The last numReadonlySignedAccounts of the signed keys are read-only accounts. Programs may process multiple transactions that load read-only accounts within a single PoH entry, but are not permitted to credit or debit lamports or modify account data. Transactions targeting the same read-write account are evaluated sequentially. */
        numReadonlySignedAccounts: z.number(),
        /** The last numReadonlyUnsignedAccounts of the unsigned keys are read-only accounts. */
        numReadonlyUnsignedAccounts: z.number(),
      })
      .optional(),
    /** A base-58 encoded hash of a recent block in the ledger used to prevent transaction duplication and to give transactions lifetimes. */
    recentBlockhash: z.string(),
    /** List of program instructions that will be executed in sequence and committed in one atomic transaction if all succeed. */
    instructions: z.array(instructionSchema),
    /** List of address table lookups used by a transaction to dynamically load addresses from on-chain address lookup tables. Undefined if maxSupportedTransactionVersion is not set. */
    addressTableLookups: z
      .array(
        z.object({
          /** base-58 encoded public key for an address lookup table account. */
          accountKey: z.string(),
          /** List of indices used to load addresses of writable accounts from a lookup table. */
          writableIndexes: z.array(z.number()),
          /** List of indices used to load addresses of readonly accounts from a lookup table. */
          readonlyIndexes: z.array(z.number()),
        }),
      )
      .optional(),
  }),
})
